const API_BASE = import.meta.env.VITE_API_URL || 'https://mawkaa-api.yassine-techini.workers.dev/api';

interface RequestOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
}

class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number,
    public details?: Record<string, string[]>
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, headers = {} } = options;

  // Get token from localStorage
  const token = localStorage.getItem('accessToken');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const config: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
  };

  if (body) {
    config.body = JSON.stringify(body);
  }

  const response = await fetch(`${API_BASE}${endpoint}`, config);

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      error: { code: 'UNKNOWN', message: 'An error occurred' },
    }));

    // Handle token expiration
    if (response.status === 401 && error.error?.code === 'TOKEN_EXPIRED') {
      // Try to refresh token
      const refreshed = await refreshToken();
      if (refreshed) {
        // Retry the request
        return request<T>(endpoint, options);
      }
      // Redirect to login
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      window.location.href = '/login';
    }

    throw new ApiError(
      error.error?.code || 'UNKNOWN',
      error.error?.message || 'An error occurred',
      response.status,
      error.error?.details
    );
  }

  return response.json();
}

async function refreshToken(): Promise<boolean> {
  const refreshTokenValue = localStorage.getItem('refreshToken');
  if (!refreshTokenValue) return false;

  try {
    const response = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: refreshTokenValue }),
    });

    if (!response.ok) return false;

    const data = await response.json();
    localStorage.setItem('accessToken', data.accessToken);
    return true;
  } catch {
    return false;
  }
}

// Convenience methods
export const api = {
  get: <T>(endpoint: string) => request<T>(endpoint),
  post: <T>(endpoint: string, body?: unknown) => request<T>(endpoint, { method: 'POST', body }),
  put: <T>(endpoint: string, body?: unknown) => request<T>(endpoint, { method: 'PUT', body }),
  patch: <T>(endpoint: string, body?: unknown) => request<T>(endpoint, { method: 'PATCH', body }),
  delete: <T>(endpoint: string) => request<T>(endpoint, { method: 'DELETE' }),
};

// Upload helper
export async function uploadFile(file: File, siteId?: string): Promise<{ url: string }> {
  const token = localStorage.getItem('accessToken');
  const formData = new FormData();
  formData.append('file', file);
  if (siteId) formData.append('siteId', siteId);

  const response = await fetch(`${API_BASE}/media/upload`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new ApiError(
      error.error?.code || 'UPLOAD_FAILED',
      error.error?.message || 'Upload failed',
      response.status
    );
  }

  return response.json();
}

export { ApiError };
